import {
  Box3,
  BoxGeometry,
  Color,
  DoubleSide,
  Group,
  MathUtils,
  Matrix4,
  Mesh,
  MeshPhongMaterial,
  Plane,
  RepeatWrapping,
  Vector3,
} from 'three';
import { getContainerBox, getGeometryBox, getGeometrySize, getObjectSize } from '../utils/objectSize';
import { sliceByPlanes } from '../utils/sliceByPlanes';
import { applyMatrixToGeometry, applyMatrixToGeometryRecursive } from '../utils/applyMatrixToGeometry';
import {
  addData,
  cloneElement,
  getWrappedObject,
  repeatElement,
  toBottom,
  toTop,
} from '../constructionTools';
import { createSampleElements, createSampleTypedSegments } from '../sampleElements';
import { isPVCRoof, isTextileRoof, isThermoRoof } from '../../App/DataStructures/roofTypes';
import { noRoofWindowType } from '../../App/DataStructures/roofWindowTypes';
import { getRangeObjectSize, isRangeObject } from '../../App/Selectors/RangeObject';
import {
  DOOR,
  DOOR_CANOPY,
  GABLE,
  GATE,
  GLASS_ELEMENT,
  ROOF,
  WALL,
  WALL_LIGHTBAND,
  WINDOW,
} from '../../Constants/ObjectTypes';
import { BACK, FRONT, LEFT, RIGHT } from '../../Constants/Sides';
import { BOTTOM, MODEL_NAME, TOP } from '../Constants';
import { isPVCWall } from '../../App/DataStructures/wallTypes';
import { createModelLoader } from './modelLoader';
import { addObjectToSegmentBase, addObjectToWallBase } from './builderHelpers';

const zAxis = Object.freeze(new Vector3(0, 0, 1));
const halfPI = Math.PI * 0.5;

// 1 unit = 1 meter
const defaultWallSegmentWidth = 5;
const roofTiltAngle = 18;
const roofEavesOverhang = 0.18;
const roofBottomOffset = 0.03;
const roofProfileHeightCorrection = 0.0098;
const profileOffset = 0.03;

// virtual hole settings
const holeRenderOrder = 1;
const wallSegmentRenderOrder = 2;

function objectNeedsHole(object) {
  return [WINDOW, WALL_LIGHTBAND, GLASS_ELEMENT, DOOR, GATE].indexOf(object.type) !== -1;
}

const fixedPositionByType = {
  [GATE]: {
    type: BOTTOM,
  },
  [DOOR]: {
    type: BOTTOM,
  },
  [WALL_LIGHTBAND]: {
    type: TOP,
    offset: 0.5,
  },
  [GLASS_ELEMENT]: {
    type: BOTTOM,
  },
  [WINDOW]: {
    type: BOTTOM,
    offset: 1,
  },
};

const loadedPrototypes = {
  ...createSampleElements(),
};

const typedSegments5m = {
  wall_trapezoidal: null,
  ...createSampleTypedSegments(5),
};

const typedSegments4m = {
  wall_trapezoidal: null,
  ...createSampleTypedSegments(4),
};

const elementPrototypes = {
  // switchable elements
  xWallSegment: null,
  zWallSegment: null,
  roofSegment: null,

  // default elements
  ...loadedPrototypes,
};

export function createGround() {
  const ground = cloneElement(elementPrototypes.ground);
  ground.receiveShadow = true;
  return ground;
}

function createWall(wallSegmentPrototype, segmentHeight = 1, blockCount = 1, data = {}, cloneOptions = {}) {
  const block = cloneElement(wallSegmentPrototype, { cloneMaterial: true, cloneTexture: true });
  cloneOptions?.removeBars && removeBars(block);
  const size = getObjectSize(block);
  const scale = segmentHeight / size.y;
  block.scale.set(1, scale, 1);

  block.traverse((obj) => {
    const texture = obj.material?.map;
    if (texture) {
      texture.wrapS = RepeatWrapping;
      texture.wrapT = RepeatWrapping;
      texture.repeat.set(1, scale);
    }
    if (obj.isMesh) {
      obj.renderOrder = wallSegmentRenderOrder;
    }
  });

  const wall = repeatElement(block, blockCount, { type: WALL, blockCount, ...data }, cloneOptions);
  wall.position.y = segmentHeight * 0.5;
  removeLastRoofBar(wall);
  return wall;
}

function removeBars(element) {
  let bar;
  while ((bar = element.getObjectByName('bar'))) {
    bar && bar.parent.remove(bar);
  }
}

function createGable(segmentWidth, blockCount = 1) {
  const angleRad = MathUtils.degToRad(roofTiltAngle);
  const width = segmentWidth * blockCount;
  const halfWidth = width * 0.5;
  const height = Math.tan(angleRad) * halfWidth;

  const distanceFromOrigin = Math.sin(angleRad) * halfWidth;
  const leftPlane = new Plane(new Vector3(0, -1, 0).applyAxisAngle(zAxis, angleRad), distanceFromOrigin);
  const rightPlane = new Plane(new Vector3(0, -1, 0).applyAxisAngle(zAxis, -angleRad), distanceFromOrigin);

  const wall = createWall(elementPrototypes.zWallSegment, height, blockCount, {}, { removeBars: true });

  wall.position.y = height * 0.5;

  const gable = new Group();
  gable.add(wall);
  sliceByPlanes(gable, [leftPlane, rightPlane]);

  addData(gable, {
    type: GABLE,
  });

  return gable;
}

function createPVCGable(roof, segmentWidth) {
  const { x: roofLength } = getObjectSize(roof);

  const pvcTopBox = getGeometryBox(roof.getObjectByName('pvc_top'));

  const pvcTopMaxY = pvcTopBox.max.y;
  const roofMinY = new Box3().setFromObject(roof).min.y;
  const height = pvcTopMaxY - roofMinY;

  const width = pvcTopBox.max.z * 2;
  const angleRad = MathUtils.degToRad(roofTiltAngle);
  const halfWidth = width * 0.5;

  const distanceFromOrigin = Math.sin(halfPI - angleRad) * height;
  const leftTiltPlane = new Plane(new Vector3(0, -1, 0).applyAxisAngle(zAxis, angleRad), distanceFromOrigin);

  const rightTiltPlane = new Plane(
    new Vector3(0, -1, 0).applyAxisAngle(zAxis, -angleRad),
    distanceFromOrigin
  );
  const leftSidePlane = new Plane(new Vector3(1, 0, 0), halfWidth);
  const rightSidePlane = new Plane(new Vector3(-1, 0, 0), halfWidth);

  const roofSegmentPrototype = cloneElement(elementPrototypes.roofSegment, { cloneGeometry: true });
  removeBars(roofSegmentPrototype);

  roofSegmentPrototype.geometry.center();
  const wall = createWall(roofSegmentPrototype, height, Math.floor(width / segmentWidth) + 2);

  const gable = new Group();
  gable.add(wall);

  sliceByPlanes(gable, [leftTiltPlane, rightTiltPlane, leftSidePlane, rightSidePlane]);

  addData(gable, {
    type: GABLE,
  });

  gable.rotation.y = halfPI;
  gable.position.x = roofLength * 0.5 - getObjectSize(roofSegmentPrototype).z * 0.5;
  gable.position.y = pvcTopMaxY - height;

  const rearGable = gable.clone();
  rearGable.rotation.y *= -1;
  rearGable.position.x *= -1;

  roof.add(gable, rearGable);
}

function removeLastRoofBar(roofGroup) {
  const roofElements = roofGroup.children;
  const lastBar = roofElements[roofElements.length - 1].getObjectByName('bar');
  lastBar && lastBar.parent.remove(lastBar);
}

function createRoof(segmentWidth, width, length, height = 1, options = {}) {
  const angleRad = MathUtils.degToRad(roofTiltAngle);
  const halfWidth = width * 0.5;

  const roofPrototype = cloneElement(elementPrototypes.roofSegment, { cloneGeometry: true });
  const size = getGeometrySize(roofPrototype);

  const bar = roofPrototype.getObjectByName('bar');
  const barSize = bar && getGeometrySize(bar);

  const roofSegmentDepthBasis = barSize?.z || size.z;

  const roofSegmentDepthCorrection = Math.tan(angleRad) * roofSegmentDepthBasis;
  const roofSegmentHeight = halfWidth / Math.cos(angleRad) + roofEavesOverhang + roofSegmentDepthCorrection;
  const roofHeight = Math.tan(angleRad) * halfWidth;
  const roofData = { type: ROOF, hasContextMenu: true };
  const cloneOptions = { cloneMaterial: false, cloneGeometry: false };
  const segmentCount = Math.round(length / segmentWidth);

  const roofGeometryScale = roofSegmentHeight / size.y;
  const scaleZ =
    roofPrototype.userData.name === 'thermo_plane'
      ? roofGeometryScale > 1
        ? (roofGeometryScale - 1) * 0.5 + 1
        : roofGeometryScale
      : 1;
  roofPrototype.geometry.applyMatrix4(new Matrix4().makeScale(1, roofGeometryScale, scaleZ));
  roofPrototype.position.y = -roofEavesOverhang * 0.5;

  if (bar) {
    bar.geometry.applyMatrix4(new Matrix4().makeScale(1, roofSegmentHeight / barSize.y, 1));
    bar.position.y = Math.tan(angleRad) * barSize.z;
  }

  const leftRoof = repeatElement(roofPrototype, segmentCount, roofData, cloneOptions);
  addData(leftRoof, {
    type: ROOF,
    side: 'left',
  });
  leftRoof.rotateX(-halfPI + angleRad);
  leftRoof.position.z = width * 0.25;
  options?.isTypePVC ? createStartBar(leftRoof) : removeLastRoofBar(leftRoof);

  const rightRoof = repeatElement(roofPrototype, segmentCount, roofData, cloneOptions);
  addData(rightRoof, {
    type: ROOF,
    side: 'right',
  });
  rightRoof.rotateY(Math.PI);
  rightRoof.rotateX(-halfPI + angleRad);
  rightRoof.position.z = -width * 0.25;
  options?.isTypePVC ? createStartBar(rightRoof) : removeLastRoofBar(rightRoof);

  const roofEdgeProfiles = options?.isTypePVC
    ? []
    : createRoofEdgeProfiles(width, length, roofSegmentHeight - roofSegmentDepthCorrection, options);

  const roof = new Group();
  roof.add(leftRoof, rightRoof, ...roofEdgeProfiles);

  if (elementPrototypes.roofProfile) {
    const topProfile = createTopRoofProfile(length);
    topProfile.position.y = roofHeight * 0.5 + roofBottomOffset;
    roof.add(topProfile);
  }

  if (elementPrototypes.roofSegmentOverlapping) {
    const barSize = getGeometrySize(leftRoof.getObjectByName('bar'));
    const roofOverlappingSize = getObjectSize(elementPrototypes.roofSegmentOverlapping);
    const roofBottom = -Math.sin(angleRad) * roofSegmentHeight * 0.5;
    const roofHalfWidth = Math.cos(angleRad) * roofSegmentHeight;

    const offsetY = roofBottom - roofOverlappingSize.y * 0.5;
    const overlappingLeft = repeatElement(elementPrototypes.roofSegmentOverlapping, segmentCount);
    createStartBar(overlappingLeft);

    const offsetZ = roofHalfWidth + roofOverlappingSize.z * 0.5 - barSize.z * 1.5;
    overlappingLeft.position.z = offsetZ;
    overlappingLeft.position.y = offsetY;

    const overlappingRight = repeatElement(elementPrototypes.roofSegmentOverlapping, segmentCount);
    options?.isTypePVC ? createStartBar(overlappingRight) : removeLastRoofBar(overlappingRight);
    overlappingRight.rotation.y = Math.PI;
    overlappingRight.position.z = -offsetZ;
    overlappingRight.position.y = offsetY;
    roof.add(overlappingLeft, overlappingRight);
  }

  options?.isTypePVC && createPVCGable(roof, segmentWidth);

  roof.position.y = height + roofHeight * 0.5 + roofBottomOffset;
  return roof;
}

function createStartBar(roofGroup) {
  const roofElements = roofGroup.children;
  const bar = roofElements[0].getObjectByName('bar');
  const startBar = bar.clone();
  startBar.position.x *= -1;
  roofElements[0].add(startBar);
}

function createTopRoofProfile(length) {
  const profile = cloneElement(elementPrototypes.roofProfile);
  const g = new Group();
  g.add(profile);
  g.scale.set(length, 1, 1);
  return g;
}

function createCornerProfiles(width, length, height) {
  const halfHeight = height * 0.5;
  const halfWidth = width * 0.5;
  const halfLength = length * 0.5;

  const frontRight = cloneElement(elementPrototypes.wallProfile);
  frontRight.scale.y = height;
  frontRight.position.set(halfLength, halfHeight, -halfWidth);
  const backRight = cloneElement(elementPrototypes.wallProfile);
  backRight.rotation.y = halfPI;
  backRight.scale.y = height;
  backRight.position.set(-halfLength, halfHeight, -halfWidth);
  const backLeft = cloneElement(elementPrototypes.wallProfile);
  backLeft.rotation.y = Math.PI;
  backLeft.scale.y = height;
  backLeft.position.set(-halfLength, halfHeight, halfWidth);
  const frontLeft = cloneElement(elementPrototypes.wallProfile);
  frontLeft.rotation.y = 3 * halfPI;
  frontLeft.scale.y = height;
  frontLeft.position.set(halfLength, halfHeight, halfWidth);
  return [frontRight, backRight, backLeft, frontLeft];
}

function createRoofEdgeProfiles(width, length, roofSegmentHeight, options = {}) {
  const { bottomProfile = false } = options;
  const angleRad = MathUtils.degToRad(roofTiltAngle);

  // create corner profiles
  const profileHeight = roofSegmentHeight + roofProfileHeightCorrection;
  const halfProfileOverhang = (roofEavesOverhang - roofProfileHeightCorrection) * 0.5;
  const [backLeft, frontLeft] = createCornerProfiles(width, length, profileHeight);

  const rightGroup = new Group();
  // set y and z to center
  frontLeft.position.y = 0;
  backLeft.position.y = 0;
  frontLeft.position.z = 0;
  backLeft.position.z = 0;
  rightGroup.add(frontLeft, backLeft);

  // add bottom profile if needed
  if (bottomProfile) {
    const leftBottom = cloneElement(elementPrototypes.wallProfile);
    leftBottom.scale.y = getObjectSize(rightGroup).x - 0.016;
    leftBottom.rotation.z = halfPI;
    leftBottom.rotation.x = -halfPI;
    leftBottom.position.y = -profileHeight * 0.5 + 0.022;
    rightGroup.add(leftBottom);
  }

  // apply rotation and positioning on the group
  rightGroup.rotation.x = halfPI - angleRad;
  rightGroup.position.z = -width * 0.25;
  rightGroup.translateY(-halfProfileOverhang);

  // create the left group by cloning the right group
  const leftGroup = rightGroup.clone();
  leftGroup.position.z = -rightGroup.position.z;
  leftGroup.rotation.z = Math.PI;
  leftGroup.rotation.x = halfPI + angleRad;

  return [rightGroup, leftGroup];
}

function createRainGutter(width, length, height) {
  const rainGutterXOffset = 0.18;
  const rainGutterYOffset = -0.09;
  const halfWidth = width * 0.5;
  const halfLength = length * 0.5;
  const rainGutterLeft = new Group();
  const leftEnd = cloneElement(elementPrototypes.raingutter_left);
  const rightEnd = cloneElement(elementPrototypes.raingutter_right);
  const body = cloneElement(elementPrototypes.raingutter_body);
  const downpipe = cloneElement(elementPrototypes.raingutter_downpipe);
  const endLength = getObjectSize(leftEnd).x;
  leftEnd.position.x = -halfLength + endLength * 0.5;
  rightEnd.position.x = halfLength - endLength * 0.5;
  const bodySize = getObjectSize(body);
  body.scale.x = (length - endLength * 2) / bodySize.x;
  const downpipeSize = getObjectSize(downpipe);
  const downpipeHeight = height - bodySize.y;
  downpipe.scale.y = downpipeHeight / downpipeSize.y;
  downpipe.position.y = 0.05;
  downpipe.position.x = halfLength - downpipeSize.z * 2;

  rainGutterLeft.add(leftEnd, rightEnd, body, downpipe);
  rainGutterLeft.position.set(0, rainGutterYOffset, halfWidth + rainGutterXOffset);
  const rainGutterRight = rainGutterLeft.clone();
  rainGutterRight.scale.set(1, 1, -1);
  rainGutterRight.position.z *= -1;

  const g = new Group().add(rainGutterLeft, rainGutterRight);
  g.position.y = height;
  return g;
}

function createRoofWindow(type, segmentWidth, width, length, height) {
  const angleRad = MathUtils.degToRad(roofTiltAngle);
  const halfWidth = width * 0.5;
  const halfLength = length * 0.5;
  const roofHeight = Math.tan(angleRad) * halfWidth;
  const fromDistance = segmentWidth;
  if (type === 'dome_window') {
    const zDistance = 2;
    const yDistance = -0.5;
    const count = Math.floor((length - 2 * fromDistance) / segmentWidth);
    const g = new Group();
    for (let i = 0; i < count; i++) {
      const odd = i % 2;
      const obj = cloneElement(elementPrototypes.domeWindow);
      obj.position.z = odd ? -zDistance : zDistance;
      obj.position.x = fromDistance + segmentWidth / 2 + i * segmentWidth - halfLength;
      obj.rotateX(odd ? -angleRad : angleRad);
      g.add(obj);
    }
    g.position.y = height + roofHeight + yDistance;
    return g;
  }
  if (type === 'ridge_light_band') {
    const ridgeLightBandLength = 20;
    const count = Math.floor((length - 2 * fromDistance) / ridgeLightBandLength);
    const ridgeLightBand = repeatElement(cloneElement(elementPrototypes.ridgeLightband), count);
    ridgeLightBand.position.y = height + roofHeight;
    return ridgeLightBand;
  }
}

function createTextileRoofParts(segmentSource) {
  const roofSegment = cloneElement(segmentSource['textile_pvc_top']);
  const roofSegmentOverlapping = cloneElement(segmentSource['textile_pvc_overlapping']);
  return {
    roofSegment,
    roofSegmentOverlapping,
  };
}

function createRoofParts(segmentSource, roofType) {
  const roofSegment = cloneElement(segmentSource[roofType], { cloneMaterial: true });
  return {
    roofSegment,
    roofSegmentOverlapping: null,
  };
}

function initPrototypes() {
  Object.assign(elementPrototypes, loadedPrototypes);
}

function filterElementPrototypesByGroup(...groups) {
  return Object.values(elementPrototypes).filter(
    (object) => object && object.userData && groups.indexOf(object.userData.group) !== -1
  );
}

function setFixedObjectPosition(element, object) {
  const fixedPosition = fixedPositionByType[object.type];
  if (fixedPosition) {
    const { type, offset } = fixedPosition;
    if (type === BOTTOM) {
      toBottom(element, offset);
    } else if (type === TOP) {
      toTop(element, offset);
    }
  }
}

function localizeGroupChildrenPosition(group) {
  const center = getContainerBox(group).getCenter(new Vector3());
  group.position.copy(center);
  for (const child of group.children) {
    child.position.sub(center);
  }
}

function addHoleForObject(segment, object) {
  const objectSize = getObjectSize(object);
  const wrappedObject = getWrappedObject(segment);
  const segmentSize = getObjectSize(wrappedObject);

  const objectWidth = Math.max(objectSize.x, objectSize.z);
  const segmentDepth = Math.min(segmentSize.x, segmentSize.z);

  const geometry = new BoxGeometry(objectWidth - 0.01, objectSize.y - 0.01, segmentDepth + 0.01);
  const hole = new Mesh(geometry, new MeshPhongMaterial({ colorWrite: false }));
  hole.renderOrder = holeRenderOrder;
  object.add(hole);
}

function getChildPrototypeName(variant, element) {
  switch (variant) {
    case DOOR_CANOPY:
      return element.userData.canopyPrototype;
    default:
      return variant;
  }
}

function fixChildPosition(variant, element, childElement) {
  switch (variant) {
    case DOOR_CANOPY: {
      childElement.position.y = getObjectSize(element).y * 0.5 + getObjectSize(childElement).y * 0.5;
      break;
    }
  }
}

function addChildrenToElement(element, children) {
  children.forEach((childObject) => {
    const variant = childObject.variant || childObject.type;
    const prototypeName = getChildPrototypeName(variant, element);
    const prototype = elementPrototypes[prototypeName];
    const childElement = cloneElement(prototype, { cloneMaterial: true });
    addData(childElement, { ...childObject, editable: true });
    childElement.warehouseObjectRef = childObject;
    fixChildPosition(variant, element, childElement);
    element.add(childElement);
  });
}

function addObjectToSegment(walls, object) {
  const { element, segment } = addObjectToSegmentBase(walls, object, elementPrototypes);

  setFixedObjectPosition(element, object);

  objectNeedsHole(object) && addHoleForObject(segment, element);

  object.children?.length && addChildrenToElement(element, object.children);
}

function buildWallLightband(size, wall) {
  const {
    userData: { length, blockCount },
  } = wall;
  const segmentSize = length / blockCount;
  const width = segmentSize * size;

  const leftPart = cloneElement(elementPrototypes['wall_lightband_left'], { cloneMaterial: true });
  const rightPart = cloneElement(elementPrototypes['wall_lightband_right'], { cloneMaterial: true });

  const leftPartSize = getObjectSize(leftPart);
  const rightPartSize = getObjectSize(rightPart);
  const bodyPartSize = getObjectSize(elementPrototypes['wall_lightband_body']);
  const bodyPartMaxWidth = width - leftPartSize.x - rightPartSize.x;
  const bodyPartCount = Math.floor(bodyPartMaxWidth / bodyPartSize.x);
  const bodyPartWidth = bodyPartSize.x * bodyPartCount;

  const wallLightband = repeatElement(
    elementPrototypes['wall_lightband_body'],
    bodyPartCount,
    {},
    {
      cloneMaterial: true,
    }
  );

  leftPart.position.x = -bodyPartWidth * 0.5 - leftPartSize.x * 0.5;
  rightPart.position.x = bodyPartWidth * 0.5 + rightPartSize.x * 0.5;

  const elements = [leftPart, ...wallLightband.children, rightPart];

  // create groups by segment size

  let groupElements = [];
  let groupWidth = 0;
  const last = elements.length - 1;
  const maxGroupWidth = segmentSize + bodyPartSize.x * 0.5;
  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    const elementWidth = getObjectSize(element).x;
    const nextWidth = groupWidth + elementWidth;
    if (nextWidth > maxGroupWidth) {
      const g = new Group();
      g.add(...groupElements);
      localizeGroupChildrenPosition(g);
      wallLightband.add(g);
      groupElements = [element];
      groupWidth = elementWidth;
    } else if (last === i) {
      groupElements.push(element);
      const g = new Group();
      g.add(...groupElements);
      localizeGroupChildrenPosition(g);
      wallLightband.add(g);
    } else {
      groupElements.push(element);
      groupWidth = nextWidth;
    }
  }

  return wallLightband;
}

function addWallLightband(walls, object) {
  if (!isRangeObject(object.type)) {
    addObjectToSegment(walls, object);
    return;
  }

  const wall = walls[object.side];
  const segment = getWrappedObject(wall.children[object.indexFrom]);

  // check if wallLightBandReference exists in the segment
  if (segment.wallLightband) {
    return; // already added, skip
  }

  // get wallLightBand size
  const { from, to, size } = getRangeObjectSize(object);

  const element = buildWallLightband(size, wall);

  element.collisions = true;
  wall.add(element);

  const segmentGroups = element.children;
  for (let i = 0; i < segmentGroups.length; i++) {
    const group = segmentGroups[i];
    addData(group, { ...object, from, to, size, rangeIndex: i, editable: true });
    group.warehouseObjectRef = object;
    group.wall = wall;
    addHoleForObject(segment, group);
  }

  const positionX = (wall.children[from].position.x + wall.children[to].position.x) * 0.5;
  element.position.set(positionX, 0, segment.position.z);

  // set a reference in all crossed segments
  for (let i = from; i <= to; i++) {
    getWrappedObject(wall.children[i]).wallLightband = element;
  }

  setFixedObjectPosition(element, object);
}

function addObjectToWall(walls, object) {
  const { element, wall } = addObjectToWallBase(walls, object, elementPrototypes);
  setFixedObjectPosition(element, object);

  objectNeedsHole(object) && addHoleForObject(wall.children[0], element);

  object.children?.length && addChildrenToElement(element, object.children);
}

function addObjects(walls, objects) {
  for (const object of objects) {
    switch (object.type) {
      case WALL_LIGHTBAND: {
        addWallLightband(walls, object);
        break;
      }
      default: {
        if (object.absoluteOffset) {
          addObjectToWall(walls, object);
        } else {
          addObjectToSegment(walls, object);
        }
      }
    }
  }
}

export function toLinear(color) {
  return new Color(color).convertSRGBToLinear();
}

export function buildWarehouse(options = {}) {
  const {
    width = 15,
    length = 15,
    height = 4.2,
    xWallSegmentWidth = 5,
    zWallSegmentWidth = 5,
    objects = [],
    wallType,
    wallColor,
    roofType,
    roofColor,
    consoleColor,
    rainGutter,
    roofWindowType,
    doorColor,
    gateColor,
  } = options;

  initPrototypes();

  // z wall segment is always 5 m, use the default type list
  if (wallType && typedSegments5m[wallType.variant]) {
    elementPrototypes.zWallSegment = cloneElement(typedSegments5m[wallType.variant]);
  }

  // x wall segment can be 5 or 4 m
  const xSegmentSource = xWallSegmentWidth === 4 ? typedSegments4m : typedSegments5m;
  if (wallType && xSegmentSource[wallType.variant]) {
    elementPrototypes.xWallSegment = cloneElement(xSegmentSource[wallType.variant]);
  }
  if (isTextileRoof(roofType) || isPVCRoof(roofType) || isThermoRoof(roofType)) {
    Object.assign(elementPrototypes, createTextileRoofParts(xSegmentSource[roofType.variant]));
  } else if (roofType && xSegmentSource[roofType.variant]) {
    Object.assign(elementPrototypes, createRoofParts(xSegmentSource, roofType.variant));
  }

  if (wallColor && !isPVCWall(wallType)) {
    const color = toLinear(wallColor.value);
    elementPrototypes.xWallSegment.material.color.set(color);
    elementPrototypes.zWallSegment.material.color.set(color);
  }
  if (consoleColor) {
    const color = toLinear(consoleColor.value);
    elementPrototypes.wallProfile.material.color.set(color);
    elementPrototypes.roofProfile && elementPrototypes.roofProfile.material.color.set(color);
    elementPrototypes.floor.setColor(color);

    // apply the color to door, window and gate frames
    elementPrototypes.door.setFrameColor(color);
    filterElementPrototypesByGroup(WINDOW, WALL_LIGHTBAND).forEach((object) => object.setColor(color));
    filterElementPrototypesByGroup(GATE).forEach((object) => object.setFrameColor(color));
  }
  if (roofColor && !isTextileRoof(roofType) && !isPVCRoof(roofType) && !isThermoRoof(roofType)) {
    elementPrototypes.roofSegment.material.color.set(toLinear(roofColor.value));
  }
  if (doorColor) {
    elementPrototypes.door.setColor(toLinear(doorColor.value));
  }
  if (gateColor) {
    const color = toLinear(gateColor.value);
    filterElementPrototypesByGroup(GATE).forEach((object) => object.setColor(color));
  }

  const lengthSegments = length / xWallSegmentWidth;
  const widthSegments = width / zWallSegmentWidth;
  const halfWidth = width * 0.5;
  const halfLength = length * 0.5;

  const warehouse = new Group();

  const cloneOptions = {
    cloneMaterial: true,
  };

  const leftSide = createWall(
    elementPrototypes.xWallSegment,
    height,
    lengthSegments,
    {
      side: LEFT,
      hasContextMenu: true,
      editable: true,
      length,
    },
    cloneOptions
  );
  leftSide.position.z = halfWidth;

  const rightSide = createWall(
    elementPrototypes.xWallSegment,
    height,
    lengthSegments,
    {
      side: RIGHT,
      hasContextMenu: true,
      editable: true,
      length,
    },
    cloneOptions
  );
  rightSide.rotation.y = Math.PI;
  rightSide.position.z = -halfWidth;

  const frontSide = createWall(
    elementPrototypes.zWallSegment,
    height,
    widthSegments,
    {
      side: FRONT,
      hasContextMenu: true,
      editable: true,
      length: width,
    },
    cloneOptions
  );
  frontSide.rotation.y = halfPI;
  frontSide.position.x = halfLength;

  const backSide = createWall(
    elementPrototypes.zWallSegment,
    height,
    widthSegments,
    {
      side: BACK,
      hasContextMenu: true,
      editable: true,
      length: width,
    },
    cloneOptions
  );
  backSide.rotation.y = -halfPI;
  backSide.position.x = -halfLength;

  const cornerProfiles = createCornerProfiles(width, length, height);

  const isTypePVC = isPVCRoof(roofType);

  const roof = createRoof(xWallSegmentWidth, width, length, height, {
    isTypePVC,
    bottomProfile: !rainGutter && !isTextileRoof(roofType) && !isThermoRoof(roofType),
  });

  const wallGables = [];

  if (!isTypePVC) {
    const gableFront = createGable(zWallSegmentWidth, widthSegments);
    gableFront.rotation.copy(frontSide.rotation);
    gableFront.position.copy(frontSide.position);
    gableFront.position.y = height;

    const gableBack = gableFront.clone();
    gableBack.rotation.copy(backSide.rotation);
    gableBack.position.copy(backSide.position);
    gableBack.position.y = height;

    wallGables.push(gableFront, gableBack);
  }

  warehouse.add(...cornerProfiles, leftSide, rightSide, frontSide, backSide, roof, ...wallGables);

  if (rainGutter) {
    const rainGutterObj = createRainGutter(width, length, height);
    if (isTextileRoof(roofType) || isPVCRoof(roofType) || isThermoRoof(roofType)) {
      const box = getContainerBox(roof);
      rainGutterObj.position.y =
        box.min.y + getObjectSize(rainGutterObj.getObjectByName('raingutter_right')).y * 0.25;
    }
    warehouse.add(rainGutterObj);
  }

  if (roofWindowType && roofWindowType.identifier !== noRoofWindowType.identifier) {
    warehouse.add(createRoofWindow(roofWindowType.value, xWallSegmentWidth, width, length, height));
  }

  const walls = {
    left: leftSide,
    right: rightSide,
    front: frontSide,
    back: backSide,
  };

  warehouse.walls = walls;

  if (objects.length) {
    addObjects(walls, objects);
  }

  addData(warehouse, { name: MODEL_NAME });

  return warehouse;
}

function centerGeometryYRecursive(object, targetY) {
  if (undefined === targetY) {
    const box = getContainerBox(object);
    targetY = -(box.max.y + box.min.y) * 0.5;
  }
  if (object.geometry) {
    object.geometry.applyMatrix4(new Matrix4().makeTranslation(0, targetY, 0));
  }
  for (const child of object.children) {
    centerGeometryYRecursive(child, targetY);
  }
}

function centerGeometryXRecursive(object) {
  if (object.geometry) {
    const box = getContainerBox(object);
    const targetX = -(box.max.x + box.min.x) * 0.5;
    try {
      object.geometry.applyMatrix4(new Matrix4().makeTranslation(targetX, 0, 0));
    } catch (e) {
      console.error(e);
    }
  }
  for (const child of object.children) {
    centerGeometryXRecursive(child);
  }
}

function fixWallMaterial(material) {
  material.side = DoubleSide;
}

function fixDoubleSideRecursive(obj) {
  obj.traverse((obj) => {
    if (obj.material) {
      obj.material.side = DoubleSide;
    }
  });
}

import { moveToCenter, moveToWorld, withContainer } from './modelUsageHelpers';

export function createWarehouseLoader(options = {}) {
  const { addHandler, loadAll } = createModelLoader('3d/draco/warehouse_models/', options);

  addHandler('wall_trapezoidal', (gltf) => {
    const model = gltf.scene.getObjectByName('wall_trapezoidal');
    fixDoubleSideRecursive(model);
    const preparedModel = withContainer(moveToCenter(moveToWorld(model), { y: true }));
    typedSegments5m.wall_trapezoidal = preparedModel;
  });

  addHandler('wall_pvc', (gltf) => {
    const model = gltf.scene.getObjectByName('wall_pvc');
    const preparedModel = withContainer(moveToCenter(moveToWorld(model), { y: true, z: false }));
    typedSegments5m.wall_pvc = preparedModel;
  });

  addHandler('wall_sandwich_40', (gltf) => {
    const model = gltf.scene.getObjectByName('wall_sandwich_40');
    fixDoubleSideRecursive(model);
    const preparedModel = withContainer(moveToCenter(moveToWorld(model), { y: true }));
    typedSegments5m.wall_sandwich_40 = preparedModel;
  });

  addHandler('wall_sandwich_60', (gltf) => {
    const model = gltf.scene.getObjectByName('wall_sandwich_060');
    fixDoubleSideRecursive(model);
    const preparedModel = withContainer(moveToCenter(moveToWorld(model), { y: true }));
    typedSegments5m.wall_sandwich_60 = preparedModel;
  });

  addHandler('profile', (gltf) => {
    const profile = gltf.scene.getObjectByName('profile_500');
    applyMatrixToGeometry(profile);
    const { geometry } = profile;
    const size = getObjectSize(profile);
    geometry.center();
    geometry.applyMatrix4(new Matrix4().makeScale(1, 1 / size.y, 1));
    geometry.applyMatrix4(new Matrix4().makeRotationY(Math.PI));
    geometry.applyMatrix4(
      new Matrix4().makeTranslation(-size.x / 2 + profileOffset, 0, size.z / 2 - profileOffset)
    );
    loadedPrototypes.wallProfile = profile;
  });

  addHandler('profile_rooftop', (gltf) => {
    applyMatrixToGeometryRecursive(gltf.scene.children[0]);
    const object = gltf.scene.getObjectByName('profile_rooftop_500');
    const { geometry } = object;
    const size = getObjectSize(object);
    geometry.center();
    geometry.applyMatrix4(new Matrix4().makeScale(1 / size.x, 1, 1));
    geometry.applyMatrix4(new Matrix4().makeTranslation(0, -0.06, 0));
    geometry.computeVertexNormals();
    object.material.side = DoubleSide;
    loadedPrototypes.roofProfile = object;
  });

  addHandler('rolling_gate', (gltf) => {
    const { scene } = gltf;
    const rolling_gate = prepareGate(scene, 'rolling_door_open');

    Object.assign(loadedPrototypes, {
      rolling_gate,
    });
  });

  addHandler('sectional_gate', (gltf) => {
    const { scene } = gltf;
    const sectional_gate = prepareGate(scene, 'sectional_gate_open_488x400');

    Object.assign(loadedPrototypes, {
      sectional_gate,
    });
  });

  addHandler('highspeed_gate', (gltf) => {
    const { scene } = gltf;
    const pvc_gate = prepareGate(scene, 'highspeed_gate_(488x400)');

    Object.assign(loadedPrototypes, {
      pvc_gate,
    });
  });

  addHandler('sliding_gate', (gltf) => {
    const { scene } = gltf;
    let sliding_gate = cloneElement(scene, {
      cloneMaterial: true,
    });
    sliding_gate = withContainer(moveToCenter(moveToWorld(sliding_gate), { y: true }));
    sliding_gate.children[0].position.z -= 0.1;
    addData(sliding_gate, {
      group: GATE,
    });

    Object.assign(loadedPrototypes, {
      sliding_gate,
    });
  });

  addHandler('steel_door', (gltf) => {
    const door = cloneElement(gltf.scene.getObjectByName('door_steel'));
    fixDoubleSideRecursive(door);
    applyMatrixToGeometryRecursive(door);
    centerGeometryYRecursive(door);
    addData(door, {
      group: DOOR,
      canopyPrototype: 'canopy_single',
    });
    door.setFrameColor = function () {};
    door.setColor = function () {};
    Object.assign(loadedPrototypes, {
      steel_door: door,
    });
  });

  addHandler('sliding_door', (gltf) => {
    const door = cloneElement(gltf.scene.getObjectByName('sliding_door'));
    fixDoubleSideRecursive(door);
    applyMatrixToGeometryRecursive(door);
    centerGeometryYRecursive(door);
    addData(door, {
      group: DOOR,
      canopyPrototype: 'canopy_single',
    });
    door.setFrameColor = function () {};
    door.setColor = function () {};
    Object.assign(loadedPrototypes, {
      sliding_door: door,
    });
  });

  addHandler('glass_door', (gltf) => {
    const door = cloneElement(gltf.scene.getObjectByName('glass_door'));
    fixDoubleSideRecursive(door);
    applyMatrixToGeometryRecursive(door);
    centerGeometryYRecursive(door);
    addData(door, {
      group: DOOR,
      canopyPrototype: 'canopy_double',
    });
    door.setFrameColor = function () {};
    door.setColor = function () {};
    Object.assign(loadedPrototypes, {
      glass_door: door,
    });
  });

  addHandler('canopy_single', (gltf) => {
    applyMatrixToGeometryRecursive(gltf.scene);
    const object = cloneElement(gltf.scene.getObjectByName('canopy_single'), { cloneMaterial: true });
    addData(object, {
      group: DOOR_CANOPY,
    });
    centerGeometryYRecursive(object);

    loadedPrototypes.canopy_single = object;
  });

  addHandler('canopy_double', (gltf) => {
    applyMatrixToGeometryRecursive(gltf.scene);
    const object = cloneElement(gltf.scene.getObjectByName('canopy_double'), { cloneMaterial: true });
    addData(object, {
      group: DOOR_CANOPY,
    });
    centerGeometryYRecursive(object);

    loadedPrototypes.canopy_double = object;
  });

  addHandler('windows_1leaves', (gltf) => {
    applyMatrixToGeometryRecursive(gltf.scene);
    const windowObject = cloneElement(gltf.scene.getObjectByName('window_1leaves'), { cloneMaterial: true });
    addData(windowObject, {
      group: WINDOW,
    });
    centerGeometryYRecursive(windowObject);
    centerGeometryXRecursive(windowObject);

    loadedPrototypes.window_1leaves = windowObject;
  });

  addHandler('windows_2leaves', (gltf) => {
    applyMatrixToGeometryRecursive(gltf.scene);
    const windowObject = cloneElement(gltf.scene.getObjectByName('window_2leaves'), { cloneMaterial: true });
    addData(windowObject, {
      group: WINDOW,
    });
    centerGeometryYRecursive(windowObject);
    centerGeometryXRecursive(windowObject);

    loadedPrototypes.window_2leaves = windowObject;
  });

  addHandler('windows_3leaves', (gltf) => {
    applyMatrixToGeometryRecursive(gltf.scene);
    const windowObject = cloneElement(gltf.scene.getObjectByName('window_3leaves'), { cloneMaterial: true });
    addData(windowObject, {
      group: WINDOW,
    });
    centerGeometryYRecursive(windowObject);
    centerGeometryXRecursive(windowObject);

    loadedPrototypes.window_3leaves = windowObject;
  });

  addHandler('domelight', (gltf) => {
    const domeWindow = cloneElement(gltf.scene.getObjectByName('domelight_'));
    domeWindow.translateY(-0.1);
    Object.assign(loadedPrototypes, {
      domeWindow,
    });
  });

  addHandler('wall_lightband', (gltf) => {
    applyMatrixToGeometryRecursive(gltf.scene);
    const wall_lightband = cloneElement(gltf.scene.children[0]);
    centerGeometryYRecursive(wall_lightband);
    centerGeometryXRecursive(wall_lightband);
    Object.assign(loadedPrototypes, {
      wall_lightband,
    });
  });

  addHandler('raingutter', (gltf) => {
    applyMatrixToGeometryRecursive(gltf.scene);
    centerGeometryXRecursive(gltf.scene);
    const raingutter_left = cloneElement(gltf.scene.getObjectByName('raingutter_left'));
    const raingutter_body = cloneElement(gltf.scene.getObjectByName('raingutter_body'));
    const raingutter_right = cloneElement(gltf.scene.getObjectByName('raingutter_right'));
    const raingutter_downpipe = cloneElement(gltf.scene.getObjectByName('fallrohr'));
    Object.assign(loadedPrototypes, {
      raingutter_left,
      raingutter_body,
      raingutter_right,
      raingutter_downpipe,
    });
  });

  addHandler('glass_element', (gltf) => {
    applyMatrixToGeometryRecursive(gltf.scene);
    const glass_element = cloneElement(gltf.scene.getObjectByName('glass_element'));
    centerGeometryYRecursive(glass_element);
    glass_element.scale.x = 5 / getObjectSize(glass_element).x;
    glass_element.position.z = -getObjectSize(glass_element).z * 0.5;
    applyMatrixToGeometryRecursive(glass_element);
    Object.assign(loadedPrototypes, {
      glass_element,
    });
  });

  addHandler('roof_type_trapezoidal', (gltf) => {
    applyMatrixToGeometryRecursive(gltf.scene.children[0]);
    const model = gltf.scene.getObjectByName('wall_trapezoidal_500');
    model.geometry.center();
    model.scale.set(1, 1, 1);
    model.position.set(0, 0, 0);
    applyMatrixToGeometry(model);
    fixWallMaterial(model.material);
    typedSegments5m.roof_type_trapezoidal = model;
  });

  addHandler('roof_type_sandwich_40', (gltf) => {
    applyMatrixToGeometryRecursive(gltf.scene);
    const model = gltf.scene.getObjectByName('wall_sandwich_40');
    model.geometry.center();
    model.scale.set(1, 1, 1);
    model.position.set(0, 0, 0);
    applyMatrixToGeometryRecursive(model);
    fixWallMaterial(model.material);
    typedSegments5m.roof_type_sandwich = model;
  });

  addHandler('roof_type_pvc', (gltf) => {
    Object.assign(typedSegments5m, {
      roof_type_pvc: prepareTextileRoof(gltf.scene, {
        topElementName: 'textile_pvc_top_500',
        topBarName: 'textile_bar_top_500',
        overlappingElementName: 'textile_pvc_overlapping_500_1',
        overlappingBarName: 'textile_bar_overlapping_500',
      }),
    });
  });

  addHandler('roof_type_thermo', (gltf) => {
    Object.assign(typedSegments5m, {
      roof_type_thermo: prepareTextileRoof(gltf.scene, {
        topElementName: 'thermo_plane',
        topBarName: 'textile_bar_top_501',
        overlappingElementName: 'textile_pvc_overlapping_501_1',
        overlappingBarName: 'textile_bar_overlapping_501',
      }),
    });
  });

  return {
    loadAll,
  };
}

function prepareGate(root, name) {
  applyMatrixToGeometryRecursive(root);
  const gate = cloneElement(name ? root.getObjectByName(name) : root, {
    cloneMaterial: true,
  });
  fixDoubleSideRecursive(gate);
  gate.position.set(0, 0, -0.2);
  applyMatrixToGeometryRecursive(gate);
  centerGeometryYRecursive(gate);
  addData(gate, {
    group: GATE,
  });
  gate.setFrameColor = function () {};
  gate.setColor = function () {};
  return gate;
}

function prepareTextileRoof(
  root,
  { topElementName, topBarName, overlappingElementName, overlappingBarName, width }
) {
  fixDoubleSideRecursive(root);
  applyMatrixToGeometryRecursive(root);
  const textile_pvc_top = cloneElement(root.getObjectByName(topElementName), { cloneGeometry: true });
  applyMatrixToGeometryRecursive(textile_pvc_top);
  const textile_bar_top = cloneElement(root.getObjectByName(topBarName), { cloneGeometry: true });
  textile_bar_top.name = 'bar';
  textile_bar_top.userData.noHighlight = true;
  textile_pvc_top.name = 'pvc_top';
  textile_pvc_top.position.set(0, 0, 0);
  applyMatrixToGeometryRecursive(textile_bar_top);
  textile_pvc_top.add(textile_bar_top);

  const angleRad = MathUtils.degToRad(roofTiltAngle);
  const plane = new Plane(new Vector3(0, 0, 1), 0);
  sliceByPlanes(textile_pvc_top, [plane]);

  if (width) {
    textile_pvc_top.geometry.applyMatrix4(new Matrix4().makeScale(width / defaultWallSegmentWidth, 1, 1));
  }

  textile_pvc_top.rotateX(halfPI - angleRad - 0.002);
  applyMatrixToGeometryRecursive(textile_pvc_top);
  const barDistanceZ =
    getGeometryBox(textile_pvc_top).getCenter(new Vector3()).z -
    getGeometryBox(textile_bar_top).getCenter(new Vector3()).z;
  textile_pvc_top.geometry.center();
  textile_bar_top.geometry.center();

  textile_pvc_top.geometry.translate(0, 0, barDistanceZ);

  const textileBarTopSize = getObjectSize(textile_bar_top);
  textile_bar_top.scale.set(1, 1, 1);
  textile_bar_top.position.set(getObjectSize(textile_pvc_top).x * 0.5 + textileBarTopSize.x * 0.5, 0, 0);

  const textile_pvc_overlapping = cloneElement(root.getObjectByName(overlappingElementName), {
    cloneGeometry: true,
  });
  const textile_bar_overlapping = cloneElement(root.getObjectByName(overlappingBarName), {
    cloneGeometry: true,
  });
  textile_bar_overlapping.name = 'bar';
  applyMatrixToGeometryRecursive(textile_pvc_overlapping);
  applyMatrixToGeometryRecursive(textile_bar_overlapping);
  textile_pvc_overlapping.add(textile_bar_overlapping);

  sliceByPlanes(textile_pvc_overlapping, [plane]);

  if (width) {
    textile_pvc_overlapping.geometry.applyMatrix4(new Matrix4().makeScale(width / 5, 1, 1));
  }

  textile_pvc_overlapping.position.set(0, 0, 0);
  const overlappingBarDistance = getGeometryBox(textile_pvc_overlapping)
    .getCenter(new Vector3())
    .sub(getGeometryBox(textile_bar_overlapping).getCenter(new Vector3()));
  textile_pvc_overlapping.geometry.center();
  textile_bar_overlapping.geometry.center();
  textile_pvc_overlapping.geometry.translate(0, overlappingBarDistance.y, overlappingBarDistance.z);
  textile_bar_overlapping.geometry.translate(overlappingBarDistance.x, 0, 0);

  const pvc_wall = cloneElement(textile_pvc_top, { cloneGeometry: true, cloneMaterial: true });

  return {
    textile_pvc_top,
    textile_pvc_overlapping,
    pvc_wall,
  };
}

export default {
  createLoader: createWarehouseLoader,
  createBuilding: buildWarehouse,
};
